import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {filter, map, OperatorFunction, pipe, tap, withLatestFrom} from 'rxjs';
import {Params, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {Action, Store} from '@ngrx/store';
import {ROUTER_NAVIGATED} from '@ngrx/router-store';
import {RouterActions} from '@aviary/store/router/router.actions';
import {RouterSelectors} from '@aviary/store/router/router.selectors';
import {buildValidatedRouterPathFrom} from '@aviary/store/router/router.helpers';
import {DEFAULT_PAGINATION_INFO} from '@aviary/store/pagination/pagination.types';
import {asArray, isDefined} from '@aviary/store/common/typing.helpers';
import {omitPropertiesByKeyFrom} from '@aviary/store/transformation.helpers';
import {QueryParamsSelectors} from '@aviary/store/query-params/query-params.selectors';
import {PathFor} from '@aviary/store/router/router.types';
import {ROUTER_PATH_FOR} from '@aviary/injection-tokens/app-provided.tokens';

// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- This is registered via NgRx; it's not an Angular DI tree-shakable provider.
@Injectable()
export class RouterEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly router: Router = inject(Router);
  private readonly matDialog: MatDialog = inject(MatDialog);
  private readonly store: Store = inject(Store);
  private readonly pathFor: PathFor = inject<PathFor>(ROUTER_PATH_FOR);

  public navigateTo = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.navigateTo),
      tap(({routeName, params}) => {
        this.router.navigate([buildValidatedRouterPathFrom(routeName, params, this.pathFor)]).then();
      })
    ),
    {dispatch: false}
  );

  public navigateToUrl = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.navigateToUrl),
      tap(({url}) => {
        this.router.navigate([url]).then();
      })
    ),
    {dispatch: false}
  );

  public replaceRouteWith = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.replaceRouteWith),
      tap(({routeName, params}) => {
        this.router.navigate([buildValidatedRouterPathFrom(routeName, params, this.pathFor)], {replaceUrl: true}).then();
      })
    ),
    {dispatch: false}
  );

  public updateSearchParams = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.updateSearchParams),
      concatLatestFrom(({typeParamOverride}) => [
        this.store.select(RouterSelectors.queryParamValue('q')),
        this.store.select(
          RouterSelectors.queryParamValue(
            isDefined(typeParamOverride) ? typeParamOverride : 'qType'
          )
        ),
        this.store.select(RouterSelectors.queryParamNames)
      ]),
      map(([{query, queryType, destinationRouteName, typeParamOverride, clearFilters}, lastQueryValue, lastQueryTypeValue, queryParamNames]) => {
        const queryTypeParamName: string = isDefined(typeParamOverride) ? typeParamOverride : 'qType';
        const paramNamesToRemove = [queryTypeParamName, 'q'];
        if (query !== lastQueryValue || queryType !== lastQueryTypeValue) {
          // If the query or queryType have been updated, reset the page to page one
          paramNamesToRemove.push('page');
          if (clearFilters) {
            // Filter data on searches may come from the search results. If the search query changes we may still have filter parameters that the api is no longer returning data for, so we clear them here.
            paramNamesToRemove.push(...queryParamNames.filter((param) => !['q', queryTypeParamName, 'pageSize'].includes(param)))
          }
        }
        return {
          destinationPath: this.pathFor[destinationRouteName], // Assumption: A route path used here will never have variable parameters to substitute.
          newQueryParams: {
            ...query && queryType && {[queryTypeParamName]: queryType},
            ...query && {q: query}
          },
          paramNamesToRemove // Remove any params that need updating
        };
      }),
      this.navigate()
    ),
    {dispatch: false}
  );

  public updatePaginationParams = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.updatePaginationParams),
      withLatestFrom(
        this.store.select(RouterSelectors.queryParamValueAsNumber('pageSize'))
      ),
      map(([{page, pageSize}, previousPageSize]) => {
        // Reset to the first page when the page size has changed.  Account for the fact that pageSize is omitted from parameters when it's the default value.
        const newPage: number =
          pageSize === (previousPageSize ?? DEFAULT_PAGINATION_INFO.pageSize)
            ? page
            : 1;

        return {
          newQueryParams: {
            ...newPage > DEFAULT_PAGINATION_INFO.page && {page: newPage},
            ...pageSize !== DEFAULT_PAGINATION_INFO.pageSize && {pageSize}
          },
          paramNamesToRemove: ['page', 'pageSize']
        };
      }),
      this.navigate()
    ),
    {dispatch: false}
  );

  public updateSortParams = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.updateSortParams),
      map(({angularSortState, entityId}) => {
        const parameterName: string = isDefined(entityId) ? `sort-${entityId}` : 'sort';

        return {
          newQueryParams: {
            ...angularSortState.direction && {
              [parameterName]: angularSortState.direction === 'asc'
                ? angularSortState.active
                : `${angularSortState.active},desc`
            }
          },
          paramNamesToRemove: [parameterName, 'page'] // Reset to the first page on any sorting change.
        };
      }),
      this.navigate()
    ),
    {dispatch: false}
  );

  public addFilterParam = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.addFilterParam),
      map(({name, value}) => ({
        newQueryParams: {
          [name]: value
        },
        /*
          If this filter was already present, clear it so that we update/replace it.  If it wasn't, then clearing it is a no-op anyway.
          Also, reset to the first page on any filtering change (including "search").
        */
        paramNamesToRemove: [name, 'page']
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  /*
    This 'clean' version of addFilterParam removes all query params, intended to reset the table with only the filter currently being added.
  */
  public cleanAddFilterParam = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.cleanAddFilterParam),
      withLatestFrom(this.store.select(RouterSelectors.queryParamNames)),
      map(([{name, value}, queryParamNames]) => ({
        newQueryParams: {
          [name]: value
        },
        paramNamesToRemove: queryParamNames
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  public clearAllFilters = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.clearAllFilters),
      withLatestFrom(this.store.select(RouterSelectors.queryParamNames)),
      map(([, queryParamNames]) => ({
        newQueryParams: {},
        paramNamesToRemove: queryParamNames.filter((param) => !['q', 'qType', 'searchTarget', 'pageSize'].includes(param))
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  public removeFilterParam = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.removeFilterParam),
      map(({name}) => ({
        newQueryParams: {},
        paramNamesToRemove: [name, 'page'] // Reset to the first page on any filtering change (including "search").
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  public updateSearchTermAndTargetParams = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.updateSearchTermAndTargetParams),
      map(({value, searchTarget}) => ({
        newQueryParams: {
          ['query']: value,
          ['searchTarget']: searchTarget
        },
        paramNamesToRemove: ['query', 'searchTarget', 'page']
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  public removeSearchTermAndTargetParams = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.removeSearchTermAndTargetParams),
      map(() => ({
        newQueryParams: {},
        paramNamesToRemove: ['query', 'searchTarget', 'page']
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  public removeQueryParams = createEffect(
    () => this.actions.pipe(
      ofType(RouterActions.removeQueryParams),
      map(({names}) => ({
        newQueryParams: {},
        paramNamesToRemove: asArray(names)
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  public closeModals = createEffect(
    () => this.actions.pipe(
      ofType<Action>(ROUTER_NAVIGATED),
      tap(() => this.matDialog.closeAll())
    ),
    {dispatch: false}
  );

  public addInitialQueryParams = createEffect(
    () => this.actions.pipe(
      ofType<Action>(ROUTER_NAVIGATED),

      // If this route doesn't have both 'name' and 'initialQueryParams' defined in its data, this effect doesn't apply.
      concatLatestFrom(() => [
        this.store.select(RouterSelectors.routeName),
        this.store.select(RouterSelectors.initialQueryParams)
      ]),
      filter(([, routeName, initialQueryParams]) => isDefined(routeName) && isDefined(initialQueryParams)),

      // If this route has ever been visited (as evidenced by the fact that its query parameters have been cached at some point), this effect doesn't apply.
      concatLatestFrom(([, routeName]) =>
        this.store.select(QueryParamsSelectors.hasCachedParamsFor(routeName as string))
      ),
      filter(([[, ,], hasCachedParams]) => !hasCachedParams),

      // If we got here, re-navigate to the same route, but with its initial query params attached.
      map(([[, , initialQueryParams]]) => ({
        newQueryParams: {...initialQueryParams},
        paramNamesToRemove: []
      })),
      this.navigate()
    ),
    {dispatch: false}
  );

  private navigate(): OperatorFunction<{ destinationPath?: string; newQueryParams: Params; paramNamesToRemove: string[] }, unknown> {
    return pipe(
      withLatestFrom(
        this.store.select(RouterSelectors.path),
        this.store.select(RouterSelectors.queryParams)
      ),
      tap(([{destinationPath, newQueryParams, paramNamesToRemove}, currentPath, currentQueryParams]) =>
        this.router.navigate(
          [destinationPath ?? currentPath],
          {
            queryParams: this.arrangeInPreferredOrder({
              ...newQueryParams,
              ...omitPropertiesByKeyFrom(currentQueryParams, ...paramNamesToRemove)
            })
          }
        ).then()
      )
    );
  }

  private arrangeInPreferredOrder({qType, q, sort, page, pageSize, ...otherFilters}: Params): Params {
    return {qType, q, sort, page, pageSize, ...otherFilters};
  }
}
